/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Combobox,
    CONTROL_SIZE,
    Form,
    FormValidator,
    isValid,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
} from '@veeam-vspc/components';

import type {
    WizardStep,
} from '@veeam-vspc/components';
import type { WizardStepData } from '@veeam-vspc/components/src/index';

import { PortalUserRoles } from 'core/entries/portal-user/enums';
import { StepLayout } from 'components/layouts/StepLayout';
import { useLang } from 'views/providers/LangProvider/hooks';
import { NoteBar } from 'components/layouts/NoteBar';
import { getResellerDocUrl } from '../../../Base/helpers/getResellerDocUrl';

import type { SPUserModel } from '../../interfaces';

export const stepValidate = (data: SPUserModel) => {
    const validator = new FormValidator(data);

    validator.validate('userRole').number();

    return validator.result();
};

export const Role: React.FC<WizardStepData<SPUserModel>> = ({ data, validationState, onDataChange }) => {
    const lang = useLang();

    return (
        <Form
            value={data}
            validate={stepValidate}
            validationState={validationState}
            onChange={onDataChange}
        >
            <StepLayout
                title={lang.ROLE}
                description={lang.SPECIFY_A_ROLE_TO_ASSIGN}
            >
                <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.m}>
                    <Combobox
                        name='userRole'
                        size={CONTROL_SIZE.m}
                        data={[
                            { value: PortalUserRoles.ServiceProviderAdministrator, text: lang.SERVICE_PROVIDER_ADMINISTRATOR },
                            { value: PortalUserRoles.ServiceProviderOperator, text: lang.SERVICE_PROVIDER_OPERATOR },
                            { value: PortalUserRoles.ServiceProviderUser, text: lang.SERVICE_PROVIDER_USER },
                            { value: PortalUserRoles.ServiceProviderInvoiceAuditor, text: lang.SERVICE_PROVIDER_INVOICE_AUDITOR },
                        ]}
                        valueGetter={item => item.value}
                        textGetter={item => item.text}
                        label={lang.ROLE}
                        inlineLabel
                    />

                    <NoteBar>
                        {data.userRole === PortalUserRoles.ServiceProviderAdministrator &&
                            <p>{lang.SERVICE_PROVIDER_ADMINISTRATOR_HAS_ACCESS}</p>}

                        {data.userRole === PortalUserRoles.ServiceProviderOperator &&
                            <p>{lang.SERVICE_PROVIDER_OPERATOR_CAN_PERFORM}</p>}

                        {data.userRole === PortalUserRoles.ServiceProviderUser &&
                            <p>{lang.SERVICE_PROVIDER_USER_HAS_ACCESS}</p>}

                        {data.userRole === PortalUserRoles.ServiceProviderInvoiceAuditor &&
                            <p>{lang.SERVICE_PROVIDER_INVOICE_AUDITOR_HAS_ACCESS}</p>}

                        <p>
                            {lang.CLICK}
                            {' '}

                            <a
                                href={getResellerDocUrl()}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {lang.HERE}
                            </a>

                            {' '}
                            {lang.TO_GET_DETAILED_INFORMATION}
                        </p>
                    </NoteBar>
                </StackView>
            </StepLayout>
        </Form>
    );
};

export const getRoleStep = (title: string): WizardStep<SPUserModel> => ({
    title,
    isHidden: ({ data }) => data.selfEdit || data.userRole === PortalUserRoles.ServiceProviderGlobalAdministrator,
    validate: ({ data }) => isValid(stepValidate, data),
    render: data => <Role {...data} />,
});
